// Tanstack Start server function that exposes the `listLiveInClub` use
// case to client code. On the server (SSR + the implicit HTTP endpoint
// the client invokes) it resolves the current authenticated scope,
// opens a `tenancy.withTenant(clubId, ...)` scope, and runs the
// composed use case. Returns the sectioned shape the UI renders.
//
// **File naming:** this is named `.fn.ts` rather than `.server.ts`
// because client code imports the exported callable — Tanstack Start's
// RPC bridge handles the boundary. The `.server.*` filename pattern is
// reserved for genuinely server-only modules (e.g. `composition.server.ts`)
// that client code never imports; the framework's `import-protection`
// plugin denies `.server.*` imports from client subtrees.
//
// The handler returns null when there is no active session — the route
// guard in `src/routes/_authenticated.tsx` already redirects
// unauthenticated requests to `/login`, so reaching this handler
// without a session is a defensive case (e.g. the session expires
// between page load and a refetch). The UI treats null as "redirect
// loop will pick this up on the next navigation."

import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

import type { ListLiveInClubResult } from '../domain/types';

export const listLiveInClubServerFn = createServerFn({ method: 'GET' }).handler(
	async (): Promise<ListLiveInClubResult | null> => {
		const { getServerAuthService } = await import(
			'../../authentication/composition.server'
		);
		const { getServerTenancyService } = await import(
			'../../../_shared/tenancy/composition.server'
		);
		const { getServerListLiveInClub } = await import('../composition.server');

		const { getServerObservability } = await import(
			'../../../_shared/observability/composition.server'
		);

		const auth = getServerAuthService();
		const scope = await auth.resolveTenantScope(getRequest());
		if (!scope) return null;

		const tenancy = getServerTenancyService();
		const listLiveInClub = getServerListLiveInClub();
		const observability = await getServerObservability();

		let result: ListLiveInClubResult;
		try {
			result = await tenancy.withTenant(scope.clubId, () =>
				listLiveInClub(scope.clubId),
			);
		} catch (err) {
			// Server-side failure signal (OTLP) for the manager roster fetch —
			// the backend counterpart the dashboard monitor keys on, since client
			// telemetry can be blocked by adblockers.
			observability.log({
				level: 'error',
				message: 'live-in-club.list_fetch_failed',
				attrs: { clubId: scope.clubId },
			});
			throw err;
		}

		// Stable success token so the Live In Club dashboard can chart roster-fetch
		// volume — the top of the fetch -> tap -> status_set engagement funnel.
		observability.log({
			level: 'info',
			message: 'live-in-club.list_fetched',
			attrs: { clubId: scope.clubId },
		});

		return result;
	},
);
